import { Head, Link, router } from "@inertiajs/react"
import { Clock, ArrowRight, BookOpen } from "lucide-react"
import { useMemo, useState } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"

const coursesTabs = [
  { label: "Courses", href: "/app/admin/courses" },
  { label: "Pending Review", href: "/app/admin/courses/pending_review" },
  { label: "Tracks", href: "/app/admin/learning_tracks" },
  { label: "Categories", href: "/app/admin/course_categories" },
]

function formatSubmittedAt(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-CA")
}

function pendingCompare(aVal, bVal, direction) {
  const aMissing = aVal === null || aVal === undefined
  const bMissing = bVal === null || bVal === undefined
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  if (aVal < bVal) return direction === "asc" ? -1 : 1
  if (aVal > bVal) return direction === "asc" ? 1 : -1
  return 0
}

export default function PendingReview({
  currentUser,
  courses = [],
  pendingCount,
  pendingPublicReviewCount,
}) {
  const [sortKey, setSortKey] = useState("submittedAt")
  const [direction, setDirection] = useState("asc")

  const handleSort = (column) => {
    if (sortKey !== column) {
      setSortKey(column)
      setDirection("asc")
      return
    }
    if (direction === "asc") {
      setDirection("desc")
      return
    }
    setSortKey("submittedAt")
    setDirection("asc")
  }

  const sortedCourses = useMemo(() => {
    const arr = [...courses]
    arr.sort((a, b) => {
      let aVal
      let bVal
      if (sortKey === "title") {
        aVal = a.title?.toLowerCase() ?? null
        bVal = b.title?.toLowerCase() ?? null
      } else if (sortKey === "coach") {
        aVal = a.createdBy?.name?.toLowerCase() ?? null
        bVal = b.createdBy?.name?.toLowerCase() ?? null
      } else if (sortKey === "category") {
        aVal = a.category?.name?.toLowerCase() ?? null
        bVal = b.category?.name?.toLowerCase() ?? null
      } else if (sortKey === "submittedAt") {
        aVal = a.submittedAt ? new Date(a.submittedAt).getTime() : null
        bVal = b.submittedAt ? new Date(b.submittedAt).getTime() : null
      } else if (sortKey === "lessons") {
        aVal = a.lessonsCount ?? null
        bVal = b.lessonsCount ?? null
      }
      return pendingCompare(aVal, bVal, direction)
    })
    return arr
  }, [courses, sortKey, direction])

  return (
    <AdminLayout currentUser={currentUser} pendingCount={pendingCount}>
      <Head title="Courses Pending Review - Kingdom Factor Admin" />

      <div className="space-y-6">
        <SectionTabs tabs={coursesTabs} />

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100">
            <Clock className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">
              Courses Pending Review
            </h1>
            <p className="text-zinc-500">
              {pendingPublicReviewCount ?? courses.length} course
              {(pendingPublicReviewCount ?? courses.length) === 1 ? "" : "s"}{" "}
              waiting for review
            </p>
          </div>
        </div>

        {/* Courses */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-0">
            {courses.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <SortableHeader
                      column="title"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500"
                    >
                      Title
                    </SortableHeader>
                    <SortableHeader
                      column="coach"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden md:table-cell"
                    >
                      Coach
                    </SortableHeader>
                    <SortableHeader
                      column="category"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden lg:table-cell"
                    >
                      Category
                    </SortableHeader>
                    <SortableHeader
                      column="submittedAt"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden sm:table-cell"
                    >
                      Submitted
                    </SortableHeader>
                    <SortableHeader
                      column="lessons"
                      sortKey={sortKey}
                      direction={direction}
                      onSort={handleSort}
                      className="text-zinc-500 hidden md:table-cell"
                    >
                      Lessons
                    </SortableHeader>
                    <TableHead className="text-zinc-500 text-right">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCourses.map((course) => (
                    <TableRow
                      key={course.id}
                      className="cursor-pointer border-zinc-100 hover:bg-zinc-50"
                      onClick={() =>
                        router.visit(`/app/admin/courses/${course.id}`)
                      }
                    >
                      <TableCell className="font-medium text-zinc-900">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="bg-amber-500 text-white hover:bg-amber-500">
                            Pending
                          </Badge>
                          <Link
                            href={`/app/admin/courses/${course.id}`}
                            className="hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {course.title}
                          </Link>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-500 hidden md:table-cell">
                        {course.createdBy?.name || "—"}
                      </TableCell>
                      <TableCell className="text-zinc-500 hidden lg:table-cell">
                        {course.category?.name || "—"}
                      </TableCell>
                      <TableCell className="text-zinc-500 hidden sm:table-cell">
                        {formatSubmittedAt(course.submittedAt)}
                      </TableCell>
                      <TableCell className="text-zinc-500 hidden md:table-cell">
                        {course.lessonsCount}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          href={`/app/admin/courses/${course.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50 whitespace-nowrap"
                          >
                            Review
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-zinc-400">
                <BookOpen className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium text-zinc-500">
                  No courses pending review.
                </p>
                <p className="text-sm">
                  Courses submitted by coaches for public review will appear
                  here.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}
